import React from 'react';
import {
  browserLatestPaymentDay,
  PAYMENT_METHODS,
  type PaymentDraft,
} from './paymentOnReceiptForm';
import type { PaymentOnReceiptStatus } from '../../utils/paymentOnReceipt';
import DateInput from "../../components/DateInput";

// "Has this invoice already been paid?" (D49). Shared by the upload windows and
// the review screen so the question reads the same wherever it is asked.
//
// No option is selected until the person picks one: a default of "Not paid yet"
// is how a paid invoice gets chased. The rules are utils/paymentOnReceipt.ts; this
// file only draws the fields and reports what was typed.

const OPTIONS: Array<{ value: PaymentOnReceiptStatus; label: string; hint: string }> = [
  { value: 'unpaid', label: 'Not paid yet', hint: 'We owe the supplier the full amount.' },
  { value: 'paid_in_full', label: 'Paid in full', hint: 'Already paid on receipt. It is recorded as a settled bill, not owed.' },
  { value: 'part_paid', label: 'Part paid', hint: 'Some has been paid; the rest is still owed.' },
];

const INPUT =
  'w-full px-3 py-2 rounded-lg bg-white border border-slate-200 text-slate-700 text-sm outline-none focus:ring-2 focus:ring-primary-500/25 disabled:opacity-60';

export interface PaymentOnReceiptFieldsProps {
  draft: PaymentDraft;
  onChange: (next: PaymentDraft) => void;
  /** Ask for the part-paid amount too. False at upload: the invoice has not been read yet. */
  showAmount: boolean;
  disabled?: boolean;
  /** Names the radio group, so two of these on one page do not share a selection. */
  idPrefix?: string;
  /** D109.6: false while the "Date paid" box holds a refused date; the host passes it to `checkPaymentDraft`. */
  onDateValidityChange: (valid: boolean) => void;
}

const PaymentOnReceiptFields: React.FC<PaymentOnReceiptFieldsProps> = ({
  draft,
  onChange,
  showAmount,
  disabled = false,
  idPrefix = 'payment-on-receipt',
  onDateValidityChange,
}) => {
  const set = (patch: Partial<PaymentDraft>) => onChange({ ...draft, ...patch });
  const paid = draft.status === 'paid_in_full' || draft.status === 'part_paid';

  return (
    <fieldset className="space-y-2" disabled={disabled}>
      <legend className="text-sm font-semibold text-slate-800">
        Has this invoice already been paid? <span className="text-rose-600" aria-hidden="true">*</span>
      </legend>
      <div role="radiogroup" aria-required="true" aria-label="Has this invoice already been paid?" className="grid grid-cols-1 sm:grid-cols-3 gap-2">
        {OPTIONS.map((opt) => {
          const id = `${idPrefix}-${opt.value}`;
          const selected = draft.status === opt.value;
          return (
            <label
              key={opt.value}
              htmlFor={id}
              className={`cursor-pointer rounded-lg border px-3 py-2 text-sm ${
                selected ? 'border-primary-500 bg-primary-50 ring-1 ring-primary-500/30' : 'border-slate-200 bg-white hover:bg-slate-50'
              }`}
            >
              <span className="flex items-center gap-2 font-medium text-slate-800">
                <input
                  id={id}
                  type="radio"
                  name={`${idPrefix}-status`}
                  value={opt.value}
                  checked={selected}
                  onChange={() => set({ status: opt.value })}
                />
                {opt.label}
              </span>
              <span className="mt-1 block text-xs text-slate-500">{opt.hint}</span>
            </label>
          );
        })}
      </div>

      {paid ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-1">
          <label className="block">
            <div className="text-xs text-slate-500 mb-1">Date paid *</div>
            <DateInput
              value={draft.paidDate}
              max={browserLatestPaymentDay()}
              onChange={(v) => set({ paidDate: v })}
              validity={onDateValidityChange}
              className={INPUT}
              aria-label="Date paid"
            />
          </label>
          <label className="block">
            <div className="text-xs text-slate-500 mb-1">Paid by *</div>
            <select value={draft.method} onChange={(e) => set({ method: e.target.value })} className={INPUT} aria-label="Paid by">
              <option value="">Choose…</option>
              {PAYMENT_METHODS.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </label>
          <label className="block">
            <div className="text-xs text-slate-500 mb-1">Reference (optional)</div>
            <input
              value={draft.reference}
              onChange={(e) => set({ reference: e.target.value })}
              placeholder="Cheque or transfer number"
              maxLength={120}
              className={INPUT}
              aria-label="Payment reference"
            />
          </label>
        </div>
      ) : null}

      {draft.status === 'part_paid' ? (
        showAmount ? (
          <label className="block max-w-xs">
            <div className="text-xs text-slate-500 mb-1">Amount already paid *</div>
            <input
              inputMode="decimal"
              value={draft.amount}
              onChange={(e) => set({ amount: e.target.value })}
              placeholder="0.00"
              className={`${INPUT} text-right tabular-nums`}
              aria-label="Amount already paid"
            />
          </label>
        ) : (
          <p className="text-xs text-slate-500">
            You will enter the amount already paid on the review screen, once the invoice has been read.
            Confirm &amp; sync waits for it.
          </p>
        )
      ) : null}
    </fieldset>
  );
};

export default PaymentOnReceiptFields;
